/**
 * Multi-source ranking comparison and blending (§7.3, FR-13 – FR-16).
 *
 * Every loaded ranking source is a first-class citizen here — Sleeper's own
 * ordering, FFC ADP, FantasyCalc market values, and anything the user pasted or
 * fetched via BYOR all go through the same code path (FR-53). There is no
 * privileged built-in source.
 *
 * Pure module — no I/O.
 */

import type { PlayerId, Position, RankingSource } from './types.ts';

export interface SourceRank {
  source_id: string;
  source_name: string;
  overall_rank: number;
  position_rank: number | null;
  tier: number | null;
  /** True when the rank was imputed because the player is absent (FR-15). */
  imputed: boolean;
}

/** Everything known about one player across every loaded source (FR-13). */
export interface PlayerRankView {
  player_key: PlayerId;
  ranks: SourceRank[];
  /** Rank percentile per source, 1.0 = top of that list. */
  percentiles: Map<string, number>;
  /** Blended percentile across sources, weighted (FR-15). */
  blended_percentile: number;
  /** Blended rank, 1-based, derived by sorting on blended_percentile. */
  blended_rank: number;
  /** Spread across sources — the disagreement score (FR-14). */
  disagreement: number;
}

export interface BlendResult {
  byPlayer: Map<PlayerId, PlayerRankView>;
  /** Player ids in blended order. */
  order: PlayerId[];
  /** Source ids that contributed, with the weight actually applied. */
  contributions: { source_id: string; source_name: string; weight: number; rows: number }[];
}

/**
 * Blend N ranking sources into one ordering.
 *
 * Blending happens on **rank percentile, not raw rank** (FR-15). This is not a
 * detail: a 300-player list and a 150-player list disagree by construction if
 * you average raw ranks, because rank 150 means "last" in one and "middle" in
 * the other. Percentile makes them comparable.
 *
 * Players missing from a source are imputed at that source's length + 1 and
 * flagged, so the UI can show *why* a player's blend looks odd rather than
 * silently penalising him for being absent from one list.
 */
export function blendSources(
  sources: RankingSource[],
  weights: Record<string, number>,
): BlendResult {
  const active = sources.filter((s) => s.rows.length > 0 && (weights[s.source_id] ?? 0) > 0);
  const byPlayer = new Map<PlayerId, PlayerRankView>();

  if (active.length === 0) {
    return { byPlayer, order: [], contributions: [] };
  }

  const lengths = new Map<string, number>();
  const lookup = new Map<string, Map<PlayerId, { overall: number; position: number | null; tier: number | null }>>();

  for (const source of active) {
    const map = new Map<PlayerId, { overall: number; position: number | null; tier: number | null }>();
    for (const row of source.rows) {
      if (!row.player_key) continue;
      const existing = map.get(row.player_key);
      // Defensive: a malformed source can list a player twice. Keep the better rank.
      if (!existing || row.overall_rank < existing.overall) {
        map.set(row.player_key, {
          overall: row.overall_rank,
          position: row.position_rank,
          tier: row.tier,
        });
      }
    }
    lookup.set(source.source_id, map);
    lengths.set(source.source_id, map.size);
  }

  // Union of every player named by any source.
  const allPlayers = new Set<PlayerId>();
  for (const map of lookup.values()) for (const id of map.keys()) allPlayers.add(id);

  for (const player_key of allPlayers) {
    const ranks: SourceRank[] = [];
    const percentiles = new Map<string, number>();
    let weightedSum = 0;
    let weightTotal = 0;

    for (const source of active) {
      const map = lookup.get(source.source_id)!;
      const length = Math.max(lengths.get(source.source_id) ?? 1, 1);
      const hit = map.get(player_key);
      const overall = hit?.overall ?? length + 1;
      const imputed = !hit;

      ranks.push({
        source_id: source.source_id,
        source_name: source.source_name,
        overall_rank: overall,
        position_rank: hit?.position ?? null,
        tier: hit?.tier ?? null,
        imputed,
      });

      // 1.0 at the top of the list, decreasing linearly to ~0 at the bottom.
      const percentile = clamp01(1 - (overall - 1) / length);
      percentiles.set(source.source_id, percentile);

      const weight = weights[source.source_id] ?? 0;
      weightedSum += percentile * weight;
      weightTotal += weight;
    }

    const blended = weightTotal === 0 ? 0 : weightedSum / weightTotal;

    byPlayer.set(player_key, {
      player_key,
      ranks,
      percentiles,
      blended_percentile: blended,
      blended_rank: 0, // assigned below
      disagreement: disagreementScore(ranks, lengths),
    });
  }

  const order = [...byPlayer.values()]
    .sort((a, b) => b.blended_percentile - a.blended_percentile)
    .map((v, i) => {
      v.blended_rank = i + 1;
      return v.player_key;
    });

  return {
    byPlayer,
    order,
    contributions: active.map((s) => ({
      source_id: s.source_id,
      source_name: s.source_name,
      weight: weights[s.source_id] ?? 0,
      rows: lengths.get(s.source_id) ?? 0,
    })),
  };
}

/**
 * Disagreement score (FR-14): the standard deviation of a player's percentile
 * across sources, scaled to roughly 0–100.
 *
 * Computed on percentiles rather than raw ranks for the same reason blending
 * is: "ranked 40th on a 300 list and 40th on a 150 list" is a real disagreement
 * that raw ranks would score as zero. Imputed ranks are excluded — a player
 * being absent from one list is missing data, not a strong opinion, and
 * counting it as disagreement would flag every rookie on every short list.
 */
function disagreementScore(ranks: SourceRank[], lengths: Map<string, number>): number {
  const observed = ranks.filter((r) => !r.imputed);
  if (observed.length < 2) return 0;

  const percentiles = observed.map((r) => {
    const length = Math.max(lengths.get(r.source_id) ?? 1, 1);
    return clamp01(1 - (r.overall_rank - 1) / length);
  });

  const mean = percentiles.reduce((a, p) => a + p, 0) / percentiles.length;
  const variance =
    percentiles.reduce((a, p) => a + (p - mean) ** 2, 0) / percentiles.length;
  return Math.round(Math.sqrt(variance) * 100 * 10) / 10;
}

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

/**
 * Default blend weights (FR-15), assigned by source *kind* rather than by name.
 *
 * The spec's default (Winks 0.6 / ECR 0.3 / ADP 0.1) assumes sources that v1
 * does not ship — there is no bundled expert list and FantasyPros was dropped
 * (§4.3). So the shape of the default is preserved instead of the literal
 * numbers: whatever expert list the user brings dominates, market and ADP
 * sources support it, and Sleeper's own ordering is only a floor. If the user
 * loads Winks via BYOR, he lands at 0.6 exactly as specified.
 */
export function defaultBlendWeights(sources: RankingSource[]): Record<string, number> {
  const out: Record<string, number> = {};
  const hasUser = sources.some((s) => s.kind === 'user' || s.kind === 'rank');

  for (const source of sources) {
    switch (source.kind) {
      case 'user':
      case 'rank':
        out[source.source_id] = 0.6;
        break;
      case 'market':
        out[source.source_id] = 0.3;
        break;
      case 'adp':
        out[source.source_id] = hasUser ? 0.1 : 0.5;
        break;
      default:
        out[source.source_id] = 0.1;
    }
  }

  // Sleeper's popularity ordering is a fallback, not an opinion. It carries the
  // board when nothing else has loaded and gets out of the way when something
  // better arrives (§4.3.1).
  if ('sleeper-search-rank' in out) {
    out['sleeper-search-rank'] = hasUser ? 0.05 : 0.4;
  }
  return out;
}

/** Positional rank derived from an ordering, for feeding the curve projections. */
export function positionRanks(
  order: PlayerId[],
  positionOf: (id: PlayerId) => Position | null,
): Map<PlayerId, number> {
  const counters = new Map<Position, number>();
  const out = new Map<PlayerId, number>();
  for (const id of order) {
    const pos = positionOf(id);
    if (!pos) continue;
    const next = (counters.get(pos) ?? 0) + 1;
    counters.set(pos, next);
    out.set(id, next);
  }
  return out;
}
